/**
 * Centralized attachment-upload validation.
 *
 * Three layers of checks, all done here so every upload surface (compose
 * modal, inline reply, anywhere else that hosts a file input) gets identical
 * behaviour:
 *
 *   1. Blocked file types — executable / script / installer extensions Gmail
 *      itself refuses (see https://support.google.com/mail/answer/6590).
 *
 *   2. Per-file size limits, varying by file category. A 50 MB DOCX is fine,
 *      a 50 MB PNG almost certainly isn't intentional. The caps are loose
 *      upper bounds — enough headroom that legitimate work files pass, tight
 *      enough that pathological uploads get caught client-side.
 *
 *   3. Total cumulative attachment size — where the surface has one. Email
 *      does: Gmail's 25 MB delivery ceiling, computed across both
 *      already-attached and newly-selected files, so the user does not attach
 *      10 × 5 MB files only to discover at send time that Gmail rejected the
 *      whole message. Internal notes do NOT: they are stored, not delivered,
 *      so only the per-file cap applies there.
 *
 *   4. A cap on the NUMBER of files, where the surface has one. Internal notes
 *      do: at most 10 attachments on a single note, counted across what is
 *      already stored on it and what is being added now. Email does not.
 */

// ---------------------------------------------------------------------------
// Blocklist
// ---------------------------------------------------------------------------

/** File extensions blocked from being attached. Lower-case, no leading dot. */
export const BLOCKED_ATTACHMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  // Native executables / installers
  'exe', 'msi', 'msp', 'mst', 'com', 'scr', 'cpl', 'gadget', 'application',
  'pif', 'lnk', 'inf', 'reg', 'msc',
  // Shell / batch scripts
  'bat', 'cmd', 'sh', 'bash', 'zsh', 'csh', 'ksh',
  // Windows Script Host / VBScript / JScript / WSF
  'vb', 'vbe', 'vbs', 'js', 'jse', 'jsf', 'jsfl', 'wsf', 'wsh', 'ws',
  // PowerShell
  'ps1', 'ps1xml', 'ps2', 'ps2xml', 'psc1', 'psc2', 'msh', 'msh1', 'msh2',
  'mshxml', 'msh1xml', 'msh2xml',
  // Microsoft Office macro-enabled formats (Gmail blocks these)
  'ade', 'adp', 'crt', 'cer', 'der', 'hta', 'ins', 'isp', 'its', 'mde',
  'sct', 'shb', 'shs', 'tmp', 'url', 'vsw',
  // Java / Android / mobile installers
  'jar', 'jnlp', 'apk', 'ipa', 'xap',
  // Disk images that often hide installers
  'dmg', 'iso', 'img', 'vhd', 'vhdx',
  // Misc script / executable hosts
  'py', 'pyc', 'pyo', 'pl', 'rb', 'php', 'phtml', 'php3', 'php4', 'php5',
  'phps', 'cgi', 'asp', 'aspx', 'jsp',
]);

// ---------------------------------------------------------------------------
// Size limits
// ---------------------------------------------------------------------------

const MB = 1024 * 1024;

/** Per-category per-file size cap in bytes. */
const SIZE_LIMITS: Record<FileCategory, number> = {
  image:    25 * MB,
  document: 50 * MB,
  audio:   100 * MB,
  video:   500 * MB,
  other:    50 * MB,
};

/** Gmail's outgoing-message attachment ceiling — applied as a running total
 *  across every attached file, including ones the user added earlier. */
export const TOTAL_ATTACHMENT_LIMIT = 25 * MB;

// ---------------------------------------------------------------------------
// Profiles
// ---------------------------------------------------------------------------

/**
 * One surface's rule set. Email and internal notes enforce genuinely different
 * limits — Gmail's 25MB delivery ceiling across the whole message versus a flat
 * 40MB per file with no combined cap — and used to express that by each caller
 * writing its own size check. That is how the note surfaces ended up with a
 * rounded `Math.round(size / 1048576) >= 40` boundary that disagreed with the
 * util's exact one. A profile makes the difference data rather than duplicated
 * code.
 */
export interface AttachmentProfile {
  /** Per-file cap in bytes, by category. */
  sizeLimits: Record<FileCategory, number>;
  /** Combined cap across everything on the record. 0 = no combined cap. */
  totalLimit: number;
  /**
   * Cap on how MANY files the record may hold in total. 0 / undefined = no cap.
   *
   * Counted across everything on the record, not per batch: files already
   * stored on it (passed as `existingCount`), files already picked in this
   * editor, and the batch being validated now. Picking 3 more onto a note that
   * already holds 9 accepts one and refuses two — a per-batch check would have
   * waved all three through.
   */
  maxCount?: number;
  /**
   * When set, ONLY these extensions are accepted (lower-case, no dot) — an
   * allowlist applied on top of the blocklist. Left undefined for email, which
   * accepts anything not explicitly blocked.
   */
  allowedExtensions?: ReadonlySet<string>;
  /**
   * Extra extensions refused for this surface, on top of
   * `BLOCKED_ATTACHMENT_EXTENSIONS`. For surfaces whose files are served back
   * from THIS app's origin, where markup is as dangerous as an executable.
   */
  blockedExtensions?: ReadonlySet<string>;
}

/**
 * Extensions that render as active content when served from the app's own origin.
 *
 * Mirrors `DANGEROUS_EXTENSIONS` in the API's `fileUpload` middleware — the guard
 * every note route runs (`allowActiveContent` is off there). Only the entries the
 * shared blocklist above does not already carry are listed; together the two sets
 * cover exactly what the server refuses, so the client cannot accept a file the
 * upload would then reject.
 *
 * Email does NOT get this set: its attachments go out to a recipient and are never
 * served from here, which is why that route sets `allowActiveContent`.
 */
export const ACTIVE_CONTENT_EXTENSIONS: ReadonlySet<string> = new Set([
  'html', 'htm', 'xhtml', 'shtml', 'svg', 'svgz', 'pht', 'phar', 'mjs',
]);

/** Gmail-shaped defaults — exactly what `validateAttachments()` has always applied. */
export const EMAIL_ATTACHMENT_PROFILE: AttachmentProfile = {
  sizeLimits: SIZE_LIMITS,
  totalLimit: TOTAL_ATTACHMENT_LIMIT,
};

/**
 * Internal-note attachments. Mirrors `NOTE_ATTACHMENT_LIMITS` on the API
 * (`configConstants.ts`) so the client refuses exactly what the server would
 * refuse. Keep the two in step.
 *
 * Two rules: 40MB per file, and at most 10 files on one note.
 *
 * There is still no cap on their COMBINED size — a note is stored, not
 * delivered, so the sum is not a constraint the user should be made to work
 * around. (Both the count and the total were capped once, at 10 files and
 * 100MB, and both were removed on the client and the API — see
 * `assertNoteAttachmentLimits`'s removal there. The count cap is now back by
 * request; the total-size one is deliberately not.)
 *
 * NOTE FOR THE API: this is currently a CLIENT-side rule only. The server no
 * longer asserts it, so the cap is a guard against accident, not against a
 * crafted request. Reinstating `assertNoteAttachmentLimits` with a count of 10
 * — and nothing else — would make the two agree again.
 *
 * NO type allowlist, deliberately. The note routes are configured
 * `allowedTypes: ['*']` and gate on the active-content guard alone, so an
 * allowlist here could only ever be NARROWER than the server's contract — which
 * is what it was: six extensions, which turned away the spreadsheets and
 * HEIC/HEIF the API stores and previews perfectly well
 * (`NOTE_ATTACHMENT_PREVIEW_CONVERSION_FAILED` exists for HEIC precisely because
 * it is expected). `BLOCKED_ATTACHMENT_EXTENSIONS` still refuses everything
 * executable, which is the same ground the API's guard covers.
 *
 * `Constants.NOTE_ATTACHMENTS.ACCEPT` is a separate thing: a picker HINT that
 * pre-filters the OS file dialog to the common formats. It is not validation,
 * and a file dragged in past it is judged only by the rules above.
 */
export const NOTE_ATTACHMENT_PROFILE: AttachmentProfile = {
  sizeLimits: {
    image:    40 * MB,
    document: 40 * MB,
    audio:    40 * MB,
    video:    40 * MB,
    other:    40 * MB,
  },
  totalLimit: 0,
  maxCount: 10,
  blockedExtensions: ACTIVE_CONTENT_EXTENSIONS,
};

type FileCategory = 'image' | 'document' | 'audio' | 'video' | 'other';

const IMAGE_EXTS    = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'tif', 'tiff', 'heic', 'heif']);
const DOCUMENT_EXTS = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'rtf', 'odt', 'ods', 'odp', 'md', 'json', 'xml', 'yaml', 'yml', 'log']);
const AUDIO_EXTS    = new Set(['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac', 'wma', 'opus']);
const VIDEO_EXTS    = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', '3gp', 'm4v']);

function classifyFile(ext: string): FileCategory {
  if (IMAGE_EXTS.has(ext))    return 'image';
  if (DOCUMENT_EXTS.has(ext)) return 'document';
  if (AUDIO_EXTS.has(ext))    return 'audio';
  if (VIDEO_EXTS.has(ext))    return 'video';
  return 'other';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RejectionReason = 'blocked-type' | 'too-large' | 'total-exceeded' | 'duplicate' | 'count-exceeded';

export interface RejectedAttachment {
  name: string;
  /** Extension without leading dot, lower-case (e.g. "exe"). Empty when the
   *  file had no extension. */
  extension: string;
  reason: RejectionReason;
  /** Bytes — set for too-large / total-exceeded rejections. */
  size?: number;
  /** Per-file cap that was exceeded — only set for too-large rejections. */
  maxSize?: number;
}

export interface AttachmentValidationResult {
  /** Files safe to attach. */
  accepted: File[];
  /** Files refused, with the reason recorded. */
  rejected: RejectedAttachment[];
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Returns the lower-cased extension of a filename, without the leading dot.
 *  Returns '' for files with no extension or a trailing dot. */
export function getFileExtension(filename: string): string {
  if (!filename) return '';
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return '';
  return filename.slice(dot + 1).toLowerCase();
}

/** True if the given filename's extension is on the blocklist. */
export function isBlockedAttachment(filename: string): boolean {
  const ext = getFileExtension(filename);
  return ext !== '' && BLOCKED_ATTACHMENT_EXTENSIONS.has(ext);
}

/** True when a file is an image — by MIME type first, falling back to the
 *  extension. Used to decide which dropped files get inserted inline into the
 *  editor (Gmail-style) versus added as attachments. */
export function isImageFile(file: File): boolean {
  if (file.type && file.type.startsWith('image/')) return true;
  return IMAGE_EXTS.has(getFileExtension(file.name));
}

/** Stable identity for a file, used to detect the same file being attached
 *  twice (e.g. picked then dropped, or dropped repeatedly). Name + size +
 *  last-modified is what browsers expose and is enough to treat two File
 *  objects as the same underlying file without reading their contents. */
export function fileSignature(f: File): string {
  return `${f.name} ${f.size} ${f.lastModified}`;
}

/** Human-friendly byte formatter (e.g. 1500000 → "1.5 MB", 41943040 → "40 MB").
 *  A whole number drops the ".0" so a limit reads as the round figure it is —
 *  "Each file must be 40 MB or smaller", not "40.0 MB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const oneDp = (value: number) => (Number.isInteger(value) ? `${value}` : value.toFixed(1));
  if (bytes < MB)    return `${oneDp(bytes / 1024)} KB`;
  if (bytes < 1024 * MB) return `${oneDp(bytes / MB)} MB`;
  return `${oneDp(bytes / (1024 * MB))} GB`;
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Partition newly-selected files into accepted + rejected, respecting:
 *   - the blocklist (and the profile's allowlist, where it has one)
 *   - per-file category size caps
 *   - the profile's running combined total, where it sets one
 *
 * Files are judged INDEPENDENTLY: one oversized file in a batch is the only one
 * rejected, and the rest are accepted. Where the profile caps the COUNT, the
 * batch fills the remaining room in order and the overflow is refused — so
 * dropping 5 onto a note with room for 2 attaches the first 2 and names the
 * other 3, rather than refusing the drop outright.
 *
 * Caller decides how to surface rejections (toast, inline error, etc.).
 *
 * @param newFiles      Files just picked from the input / dropped.
 * @param existingFiles Files already attached to the message (for the
 *                      running-total cap + duplicate detection). Defaults to
 *                      none.
 * @param existingBytes Extra bytes already committed to the message that
 *                      aren't represented as `File` objects — e.g. forwarded
 *                      attachments that get re-sent to the recipient. Added to
 *                      the running total but not used for duplicate matching.
 *                      Inert for a profile with no combined cap.
 * @param profile       Which rule set to apply. Defaults to the email profile,
 *                      so every existing caller keeps its current behaviour
 *                      byte for byte.
 * @param existingCount Files already on the record that aren't represented as
 *                      `File` objects — for a note, the attachments already
 *                      SAVED on it. Counts towards `profile.maxCount` but not
 *                      towards duplicate matching or the size total. Inert for
 *                      a profile with no count cap.
 */
export function validateAttachments(
  newFiles: ArrayLike<File> | null | undefined,
  existingFiles: ArrayLike<File> = [] as unknown as ArrayLike<File>,
  existingBytes: number = 0,
  profile: AttachmentProfile = EMAIL_ATTACHMENT_PROFILE,
  existingCount: number = 0,
): AttachmentValidationResult {
  const accepted: File[] = [];
  const rejected: RejectedAttachment[] = [];
  if (!newFiles) return { accepted, rejected };

  // Seed the running total — and the duplicate set — with what the message
  // already holds, so dropping/picking a file that's already attached is a
  // no-op rather than a second copy.
  let runningTotal = existingBytes > 0 ? existingBytes : 0;
  const seen = new Set<string>();
  for (let i = 0; i < existingFiles.length; i++) {
    runningTotal += existingFiles[i].size || 0;
    seen.add(fileSignature(existingFiles[i]));
  }

  // What the record already holds, for the count cap: the files already saved
  // on it plus the ones already picked in this editor.
  const maxCount = profile.maxCount ?? 0;
  let runningCount = (existingCount > 0 ? existingCount : 0) + existingFiles.length;

  for (let i = 0; i < newFiles.length; i++) {
    const f = newFiles[i];
    const ext = getFileExtension(f.name);

    // 0. Duplicate of an already-attached file, or repeated within this batch.
    const sig = fileSignature(f);
    if (seen.has(sig)) {
      rejected.push({ name: f.name, extension: ext, reason: 'duplicate' });
      continue;
    }

    // 1. Shared blocklist, then the profile's own extra blocklist, then its
    //    allowlist where it has one. All three report 'blocked-type': to the user
    //    "this kind of file isn't accepted here" is one outcome, whichever rule
    //    produced it.
    if (ext !== '' && BLOCKED_ATTACHMENT_EXTENSIONS.has(ext)) {
      rejected.push({ name: f.name, extension: ext, reason: 'blocked-type' });
      continue;
    }
    if (ext !== '' && profile.blockedExtensions?.has(ext)) {
      rejected.push({ name: f.name, extension: ext, reason: 'blocked-type' });
      continue;
    }
    if (profile.allowedExtensions && !profile.allowedExtensions.has(ext)) {
      rejected.push({ name: f.name, extension: ext, reason: 'blocked-type' });
      continue;
    }

    // 2. Per-file size cap by category. This is the ONLY size rule a note has,
    //    and it is applied per file — an oversized file removes itself from the
    //    batch and nothing else.
    const category = classifyFile(ext);
    const maxSize = profile.sizeLimits[category];
    if (f.size > maxSize) {
      rejected.push({ name: f.name, extension: ext, reason: 'too-large', size: f.size, maxSize });
      continue;
    }

    // 3. Running total cap — only where the profile sets one (email's delivery
    //    ceiling). `totalLimit: 0` means the combined size is not a constraint.
    if (profile.totalLimit > 0 && runningTotal + f.size > profile.totalLimit) {
      rejected.push({ name: f.name, extension: ext, reason: 'total-exceeded', size: f.size });
      continue;
    }

    // 4. Count cap — only where the profile sets one (a note's 10 attachments).
    //    Last, deliberately: a file that would have been refused anyway should
    //    be named for the reason it actually failed, not for filling a slot it
    //    was never going to occupy.
    if (maxCount > 0 && runningCount >= maxCount) {
      rejected.push({ name: f.name, extension: ext, reason: 'count-exceeded' });
      continue;
    }

    accepted.push(f);
    runningTotal += f.size;
    runningCount++;
    seen.add(sig);
  }

  return { accepted, rejected };
}

// ---------------------------------------------------------------------------
// Toast message builder
// ---------------------------------------------------------------------------

/**
 * Build a short, Gmail-style toast naming the rejected files. Groups
 * rejections by reason so the user sees up to three concise clauses
 * separated by " • " instead of one runaway sentence.
 */
export function buildBlockedAttachmentsMessage(
  rejected: RejectedAttachment[],
  profile: AttachmentProfile = EMAIL_ATTACHMENT_PROFILE,
): string {
  if (rejected.length === 0) return '';

  const blocked = rejected.filter((r) => r.reason === 'blocked-type');
  const tooLarge = rejected.filter((r) => r.reason === 'too-large');
  const totalExceeded = rejected.filter((r) => r.reason === 'total-exceeded');
  const duplicate = rejected.filter((r) => r.reason === 'duplicate');
  const countExceeded = rejected.filter((r) => r.reason === 'count-exceeded');

  const parts: string[] = [];
  if (blocked.length) {
    parts.push(`Blocked for security: ${blocked.map((r) => r.name).join(', ')}`);
  }
  if (tooLarge.length) {
    // Name the cap when the whole group shares one — for notes that is always
    // true (40MB flat), so the user is told the rule and not just the verdict.
    // Email's caps are per category, so a mixed group falls back to "Too large".
    const caps = new Set(tooLarge.map((r) => r.maxSize).filter((m): m is number => !!m));
    const names = tooLarge.map((r) => r.name).join(', ');
    parts.push(caps.size === 1
      ? `Each file must be ${formatBytes([...caps][0])} or smaller: ${names}`
      : `Too large: ${names}`);
  }
  if (totalExceeded.length) {
    parts.push(`Total exceeds ${formatBytes(profile.totalLimit)}: ${totalExceeded.map((r) => r.name).join(', ')}`);
  }
  if (duplicate.length) {
    parts.push(`Already attached: ${duplicate.map((r) => r.name).join(', ')}`);
  }
  if (countExceeded.length) {
    // Names the rule, not just the verdict — the user needs to know the note is
    // full, not merely that these particular files were refused.
    parts.push(`A note can hold at most ${profile.maxCount} attachments:`
      + ` ${countExceeded.map((r) => r.name).join(', ')} not added`);
  }
  return parts.join(' • ');
}
